import React, { FC, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  Divider,
  Avatar,
  Box,
  Text,
  SkeletonGroup,
  SkeletonRectangle,
  SkeletonLine,
  Heading,
  Image,
  Button,
} from '@wix/design-system';
import { ArrowRight } from '@wix/wix-ui-icons-common';
import {
  ProfessionalInformation,
  Address,
  Portfolio,
} from '@wix/ambassador-partner-profiles/types';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useIntersect } from '@wix/experts-common';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { useBi } from '@components/Hooks/useBi';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { generateLocationLabel } from '@utils/locationLabel';
import { fillProfileUrl } from '@utils/urlUtils';
import useFormattedCurrency from '@components/Hooks/useFormattedCurrency';
import { profilePath } from '@components/Hooks/useRouter';
import { ENABLE_CURRENCY_PREFERENCES_EXPERIMENT } from '@utils/currency';
import SlickCarousel from '../SlickCarousel';
import s from './Provider.module.scss';

type Props = {
  id: string;
  slug: string;
  address: Address;
  professionalInformation: ProfessionalInformation;
  providerRating: any;
  portfolio: Portfolio;
  startingFrom: number;
  forceRender?: boolean;
  autoFocus?: boolean;
};

function Star() {
  return (
    <svg
      width={19}
      height={24}
      viewBox="0 0 19 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.25 13.5l-2.83-2.793c-.302-.315-.156-.759.28-.85L7.55 9.3l1.994-3.838a.3.3 0 01.538.012L11.85 9.3l3.937.556c.281 0 .71.485.281.85L13.25 13.5l.989 4.068a.361.361 0 01-.519.39L9.75 16l-3.967 1.95c-.22.142-.611-.074-.533-.402l1-4.048z"
        fill="#FFC23D"
      />
    </svg>
  );
}

const Provider: FC<Props> = ({
  startingFrom,
  professionalInformation,
  providerRating,
  portfolio,
  address,
  forceRender,
  id,
  slug,
  autoFocus,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { sessionId } = useBiSessionContext();

  const {
    state: {
      entry,
      origin,
      currentPage,
      userCurrency,
      currencyConversionRate,
      shouldFocusOnPartnerCard,
    },
  } = useExploreContext();
  const logger = useBi();
  const { experiments } = useExperiments({ readOnly: true });
  const enableCurrencyPreferences = experiments.enabled(
    ENABLE_CURRENCY_PREFERENCES_EXPERIMENT,
  );

  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [ready, setReady] = useState(false || forceRender);
  const [ref, entryElment] = useIntersect({}) as any;
  const { startingPriceFormatter } = useFormattedCurrency({
    currency: userCurrency,
    conversionRate: currencyConversionRate,
  });

  const portfolioItemsToDisplay = useMemo(() => {
    return _.filter(portfolio?.items, (portfolioItem) => !portfolioItem.hidden);
  }, [portfolio?.items]);

  useEffect(() => {
    if (entryElment.intersectionRatio === 1 && !ready) {
      setTimeout(() => {
        setReady(true);
      }, 0);
    }
    if (ready && autoFocus && shouldFocusOnPartnerCard) {
      setTimeout(() => {
        (document.querySelector(`#provider-box-${id}`) as HTMLElement).focus();
      }, 0);
    }
  }, [ready, entryElment, autoFocus]);

  const carouselImages = useMemo(() => {
    return _.map(
      portfolioItemsToDisplay,
      ({ metaSiteId, customImage, siteName }) => {
        const generatedThumbnail = `https://www.wix.com/site-thumbnail/${metaSiteId}?preset=site-list`;
        const mediaSource = customImage
          ? fillProfileUrl(customImage, 370, 226)
          : generatedThumbnail;
        const backupSource = customImage ? generatedThumbnail : '';
        return { mediaSource, backupSource, siteName };
      },
    );
  }, [portfolioItemsToDisplay]);

  function buildPartnerPageLink() {
    const partnerLink = `${profilePath}/${slug}`;
    return !enableCurrencyPreferences && userCurrency
      ? `${partnerLink}?currency=${userCurrency}`
      : partnerLink;
  }

  function viewProfile() {
    window.location.href = buildPartnerPageLink();
  }

  function renderLoadingCard() {
    return (
      <SkeletonGroup skin="light">
        <Box
          className={s.partner}
          direction="vertical"
          padding="15px"
          dataHook={`provider-box-loading${id}`}
        >
          <div ref={ref}></div>
          <div style={{ minHeight: '171px' }}>
            <SkeletonRectangle height="171px" />
          </div>
          <div
            style={{
              position: 'relative',
              top: '-33px',
              marginLeft: '5px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                border: '1px solid #FFFFFF',
                backgroundColor: '#FFFFFF',
                boxShadow:
                  '0px 0px 6px rgba(0, 6, 36, 0.12), 0px 2px 4px rgba(0, 6, 36, 0.12)',
                borderRadius: '100px',
              }}
            >
              <Avatar
                imgProps={{
                  src: '',
                }}
                size="size72"
              />
            </div>
          </div>
          <SkeletonLine marginTop="52px" marginBottom="10px" />
          <SkeletonLine marginBottom="10px" />
          <SkeletonLine marginBottom="48px" />
          <Divider />
          <Box padding="2px" align="space-between" verticalAlign="bottom">
            <Box direction="vertical">
              <Box verticalAlign="middle" direction="vertical">
                <SkeletonLine marginTop="20px" marginBottom="10px" />
                <SkeletonLine marginBottom="5px" />
              </Box>
            </Box>
            <Box>
              <SkeletonLine marginBottom="5px" />
            </Box>
          </Box>
        </Box>
      </SkeletonGroup>
    );
  }

  function renderProviderCard() {
    return (
      <Box
        className={s.partner}
        direction="vertical"
        dataHook={`provider-box-${id}`}
      >
        <div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => {
            setHover(false);
            setFocus(false);
          }}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
        >
          <SlickCarousel
            hover={hover}
            focus={focus}
            imageSources={carouselImages}
          />

          <Divider />
          <div
            style={{
              position: 'relative',
              top: '-33px',
              marginLeft: '18px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                border: '1px solid #FFFFFF',
                backgroundColor: '#FFFFFF',
                boxShadow:
                  '0px 0px 6px rgba(0, 6, 36, 0.12), 0px 2px 4px rgba(0, 6, 36, 0.12)',
                borderRadius: '100px',
              }}
            >
              <Avatar
                dataHook={`profile-avatar-${id}`}
                imgProps={{
                  src: fillProfileUrl(
                    professionalInformation.profileImage! || '',
                    180,
                    180,
                  ),
                }}
                name={professionalInformation.businessName}
                size="size72"
              />
            </div>
          </div>
          <Box
            direction="vertical"
            paddingTop="48px"
            paddingLeft="18px"
            paddingRight="18px"
            paddingBottom="24px"
            minHeight="82px"
            maxHeight="82px"
          >
            <Text
              showTooltip
              ellipsis
              maxLines={1}
              light
              secondary
              size="small"
              weight="thin"
            >
              {generateLocationLabel(address)}
            </Text>
            <Box height="6px" />
            <Text
              id={`partner-business-name-${id}`}
              aria-hidden="true"
              size="small"
              weight="normal"
              ellipsis
              maxLines={1}
            >
              {professionalInformation.businessName}
            </Text>
            <Box height="6px" />
            <Box>
              <Text
                size="small"
                weight="thin"
                secondary
                ellipsis
                maxLines={2}
                tooltipProps={{ maxWidth: '270px' }}
              >
                {professionalInformation.tagline}
              </Text>
            </Box>
          </Box>
          <Divider />
          <Box padding="18px" align="space-between" verticalAlign="bottom">
            <Box direction="vertical">
              <Box verticalAlign="middle">
                <Star />
                <Box width="3px" />
                <Heading size="small" as="span">
                  {Intl.NumberFormat(language, {
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 1,
                  }).format(providerRating?.averageReviewRating)}
                </Heading>
                <Box width="3px" />
                <Text size="small" weight="thin">
                  (
                  {Intl.NumberFormat(language, {}).format(
                    providerRating?.reviewsCount,
                  )}
                  )
                </Text>
              </Box>
              <Text size="small" weight="thin">
                {t('explore-page-starting-from', {
                  amount: startingPriceFormatter(startingFrom),
                })}
              </Text>
            </Box>
            <Box>
              <Button
                dataHook="view-button"
                skin="dark"
                as="a"
                size="small"
                priority="secondary"
                href={buildPartnerPageLink()}
                suffixIcon={<ArrowRight />}
                onTouchEnd={() => {
                  viewProfile();
                }}
              >
                {t('provider-view')}
              </Button>
            </Box>
          </Box>
        </div>
      </Box>
    );
  }

  return (
    <div
      data-hook="provider-container"
      aria-labelledby={`partner-business-name-${id}`}
      tabIndex={-1}
      className={s.providerContainer}
      id={`provider-box-${id}`}
      onClick={() => {
        viewProfile();
        logger.report(
          exploreResultsPageGenericClick({
            clickType: 'view profile',
            sessionId,
            origin,
            entry,
            exploreVersion: 'V2',
            data: JSON.stringify({
              partnersGuid: id,
              currentPage,
            }),
          }),
        );
      }}
    >
      {ready ? renderProviderCard() : renderLoadingCard()}
    </div>
  );
};

export default Provider;
